import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../../shared/ui/button/Button";

import { FileText, History } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import TabSwitcher from "../../../../../shared/ui/tabs/TabSwitcher";
import { useMotionSafe } from "../../../../../hooks/useMotionSafe";

const PANEL_ID = "therapeutic-plan-add-panel";
import TherapeuticPlanAddBasicForm from "./TherapeuticPlanAddBasicForm";
import TherapeuticPlanAddTimeline from "./TherapeuticPlanAddTimelineForm";
import NotionEditor from "../../../../../shared/ui/form/input/NotionEditor";
import { createTherapeuticPlan } from "../../../application/therapeuticPlans";
import { useNavigate, useSearchParams } from "react-router";

import type { TherapeuticPhase } from "./therapeuticPhaseDraft";

interface TherapeuticPlanAddFormProps {
  therapistID: string;
}

export default function TherapeuticPlanAddForm({
  therapistID,
}: TherapeuticPlanAddFormProps) {
  const { t } = useTranslation();
  const { reduced } = useMotionSafe();
  const navigate = useNavigate();

  // -------- BASIC INFO --------
  const [searchParams] = useSearchParams();
  const preselectedPatient = searchParams.get("patient") ?? "";
  const preselectedCouple = searchParams.get("couple") ?? "";

  const [patientId, setPatientId] = useState(preselectedPatient);
  const [couplePatientId, setCouplePatientId] = useState<string | undefined>(preselectedCouple || undefined);
  const [type, setType] = useState<'individual' | 'couple'>(preselectedCouple ? 'couple' : 'individual');

  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");

  // -------- PHASES --------
  const [phases, setPhases] = useState<TherapeuticPhase[]>([]);
  const [saving, setSaving] = useState(false);

  // -------- TABS --------
  const [activeTab, setActiveTab] = useState<'details' | 'timeline'>('details');

  const tabs = [
    { id: 'details', label: t("therapeuticPlan.tabs.details", "Details"), icon: FileText },
    { id: 'timeline', label: t("therapeuticPlan.tabs.timeline", "Timeline"), icon: History },
  ];

  useEffect(() => {
    if (!patientId && !couplePatientId) return;
    setPhases([]);
  }, [patientId, couplePatientId]);

  // -------- SAVE --------
  const mapPhasesToTimeline = (phases: TherapeuticPhase[]) => {
    return phases
      .sort((a, b) => a.order - b.order)
      .map((phase) => ({
        title: phase.title,
        description: phase.description,
        internal_notes: phase.notes ?? "",
        linked_sessions: phase.sessions,
        completed: phase.done,
        order: phase.order,
      }));
  };

  const handleSave = async () => {
    if ((type === 'individual' && !patientId) || (type === 'couple' && !couplePatientId) || !title) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      title,
      initial_context: context,
      patient_id: type === 'individual' ? patientId : undefined,
      couple_patient_id: type === 'couple' ? couplePatientId : undefined,
      therapist_id: therapistID,
      timeline: mapPhasesToTimeline(phases),
    };

    try {
      setSaving(true);
      console.log("Creating therapeutic plan:", payload);

      await createTherapeuticPlan(payload);
      navigate("/therapeutic-plans");
    } catch (error) {
      console.error("Error creating therapeutic plan:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Centered Pill Navigation */}
      <TabSwitcher
        tabs={tabs}
        activeTab={activeTab}
        onChange={(id) => setActiveTab(id as typeof activeTab)}
        label={t("therapeuticPlan.tabs.label", "Plan sections")}
        layoutId="activeTabPlanAdd"
        panelId={PANEL_ID}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          id={PANEL_ID}
          role="tabpanel"
          initial={{ opacity: 0, y: reduced ? 0 : 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduced ? 0 : -6 }}
          transition={{ duration: reduced ? 0.12 : 0.2 }}
        >
          {/* DETAILS TAB */}
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Left Column: Config & Actions */}
              <div className="lg:col-span-4 lg:sticky lg:top-8 space-y-6">
                <TherapeuticPlanAddBasicForm
                  therapistID={therapistID}
                  patientId={patientId}
                  couplePatientId={couplePatientId}
                  type={type}
                  onPatientChange={(id) => setPatientId(id)}
                  onCoupleChange={(id) => setCouplePatientId(id)}
                  onTypeChange={(t) => setType(t)}
                  title={title}
                  onTitleChange={setTitle}
                />
              </div>

              {/* Right Column: Initial Context (Spacious Editor) */}
              <div className="lg:col-span-8">
                <div className="glass-effect glass-border border rounded-3xl p-8 shadow-theme-sm space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center">
                      <FileText className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold text-content-primary">
                      {t("therapeuticPlan.initialContext")}
                    </h3>
                  </div>

                  <div className="rounded-2xl border border-gray-100 dark:border-white/5 bg-white/40 dark:bg-white/[0.02] overflow-hidden min-h-[300px]">
                    <NotionEditor
                      value={context}
                      onChange={setContext}
                    />
                  </div>
                </div>
              </div>

              {/* Bottom Actions Bar */}
              <div className="col-span-full flex flex-row-reverse gap-3 pt-6 border-t mt-4 border-gray-100 dark:border-white/5">
                <Button
                  onClick={handleSave}
                  disabled={((type === 'individual' && !patientId) || (type === 'couple' && !couplePatientId)) || !title}
                  loading={saving}
                  className="w-full max-w-[200px] shadow-lg shadow-brand-500/20 py-3 font-bold"
                >
                  {saving ? t("therapeuticPlan.creating") : t("therapeuticPlan.save")}
                </Button>
              </div>
            </div>
          )}

          {/* TIMELINE TAB */}
          {activeTab === 'timeline' && (
            <div className="max-w-5xl mx-auto">
              <TherapeuticPlanAddTimeline
                patientId={patientId}
                couplePatientId={couplePatientId}
                type={type}
                phases={phases}
                onChange={setPhases}
              />
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
